import { toClassificationDialogData } from '@content-search/adapters';
import type { ContentCardItem } from '@content-search/domain';
import { useStandardLabels } from '@content-search/hooks/useStandardLabels';
import { ClassificationDialog } from '@core/components';
import { ContentCard as DesignSystemContentCard, Button, ButtonSize, ButtonType } from '@soteria/design-system-ve';
import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { useMemo, useState } from 'react';

import * as styles from './ContentCard.css';
import { SearchStyleContentSummary } from './SearchStyleContentSummary';

export interface SearchResultCardProps {
  item: ContentCardItem;
  onClick?: () => void;
  mediaServiceUrl?: string;
  /** Hide the "Show Conversation Context" button (used when already in conversation context view) */
  hideConversationContext?: boolean;
  /** Optional content to render at the top of the card */
  headerContent?: ReactNode;
}

/**
 * Displays a content search result card with summary, expandable details, and action controls.
 * Uses the design system ContentCard as the visual container.
 */
export function SearchResultCard({ item, onClick, mediaServiceUrl, hideConversationContext, headerContent }: SearchResultCardProps) {
  const { labelContentTypeMap } = useStandardLabels();
  const [showDialog, setShowDialog] = useState(false);
  const isSafeContent = item.classificationResult !== 'rejected';
  const [isContentBlurred, setIsContentBlurred] = useState(!isSafeContent);

  const handleCardClick = () => {
    setShowDialog(true);
  };

  const handleShowContext = (e: MouseEvent) => {
    e.stopPropagation();
    onClick?.();
  };

  const handleToggleBlur = (e?: MouseEvent) => {
    e?.stopPropagation();
    setIsContentBlurred(prev => !prev);
  };

  // Convert ContentCardItem to ClassificationDialogData for the core dialog
  // Pass label metadata to derive per-label content type when the upstream API doesn't include it
  const dialogData = useMemo(() => toClassificationDialogData(item, labelContentTypeMap), [item, labelContentTypeMap]);

  // Hide the context button when onClick is not provided (e.g., username content has no conversation context)
  const showConversationContext = !!onClick;

  const handleCardKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleCardClick();
    }
  };

  return (
    <>
      <div className={`${styles.cardClickWrapper} ${styles.contentCard()}`} role="button" tabIndex={0} onClick={handleCardClick} onKeyDown={handleCardKeyDown}>
        <DesignSystemContentCard className={styles.contentCard()} padding="none">
          <SearchStyleContentSummary
            customHeaderContent={headerContent}
            isContentBlurred={isContentBlurred}
            item={item}
            mediaServiceUrl={mediaServiceUrl}
            onToggleBlur={handleToggleBlur}
          />
          {showConversationContext && !hideConversationContext && (
            <div className={styles.centeredActionContainer}>
              <Button buttonSize={ButtonSize.Accelerator} buttonType={ButtonType.Tertiary} onClick={handleShowContext}>
                Show Conversation Context
              </Button>
            </div>
          )}
        </DesignSystemContentCard>
      </div>

      <ClassificationDialog
        data={dialogData}
        isContentBlurred={isContentBlurred}
        isOpen={showDialog}
        onClose={() => setShowDialog(false)}
        onToggleBlur={() => setIsContentBlurred(prev => !prev)}
      />
    </>
  );
}
